import { graphql, useStaticQuery } from "gatsby";
import type { IGatsbyImageData } from "gatsby-plugin-image";

type RasterEntry = {
    rel: string;
    base: string;
    gatsby: IGatsbyImageData;
    publicURL: string | null;
};

type PublicEntry = {
    rel: string;
    base: string;
    publicURL: string | null;
};

const stripLeading = (s: string) => s.replace(/^[./]+/, "");
const isHttp = (s: string) => /^https?:\/\//i.test(s);

// Add this helper near the top
const returnAsPath = (u?: string): string => {
    if (!u) return "";
    // If Gatsby/Sharp returned a root-relative path, keep it — works in dev & prod.
    if (u.startsWith("/")) return u;
    // If it's already absolute (Cloudinary, etc.), return as-is.
    if (/^https?:\/\//i.test(u)) return u;
    // Make plain relatives site-relative.
    return `/${u.replace(/^\.?\//, "")}`;
};

/** Pick the smallest >= target from a srcSet; if none >= target, return the largest. */
function chooseSrcForWidth(srcSet: string | undefined, target = 96): string | null {
    if (!srcSet) return null;
    const pairs = srcSet
        .split(",")
        .map((s) => s.trim())
        .map((s) => {
            const parts = s.split(/\s+/);
            const url = parts[0];
            const w = Number((parts[1] || "").replace("w", ""));
            return { url, w };
        })
        .filter((p) => p.url && Number.isFinite(p.w))
        .sort((a, b) => a.w - b.w);
    if (!pairs.length) return null;
    for (const p of pairs) if (p.w >= target) return p.url;
    return pairs[pairs.length - 1].url;
}

/** Get srcSet (webp first if available) and choose the right width. */
function pickThumbFromGatsby(
    data: IGatsbyImageData,
    opts?: { width?: number; preferWebp?: boolean },
): string | null {
    const width = opts?.width ?? 96;
    const preferWebp = opts?.preferWebp ?? true;

    // TS note: IGatsbyImageData has a runtime "images" object with fallback/srcSet and sources
    const images: any = (data as any)?.images;
    if (!images) return null;

    const webpSet =
        images.sources?.find((s: any) =>
            Array.isArray(s.type) ? s.type.includes("image/webp") : s.type === "image/webp",
        )?.srcSet ?? images.sources?.find((s: any) => s.type === "image/webp")?.srcSet;

    const chosenWebp = preferWebp ? chooseSrcForWidth(webpSet, width) : null;
    const chosenFallback = chooseSrcForWidth(images.fallback?.srcSet, width);
    return chosenWebp || chosenFallback || null;
}

/** Hook: dedicated thumbnail resolver for global search */
export function useSearchThumbs() {
    const data = useStaticQuery(graphql`
        query SearchThumbs_ImagesOnly {
            site {
                siteMetadata {
                    siteUrl
                }
            }
            # Raster images processed by Sharp (we'll get srcSet with small breakpoints)
            images: allFile(
                filter: { extension: { in: ["jpg", "jpeg", "png", "webp", "gif", "avif"] } }
            ) {
                nodes {
                    relativePath
                    name
                    extension
                    publicURL
                    childImageSharp {
                        gatsbyImageData(
                            layout: CONSTRAINED
                            placeholder: NONE
                            quality: 85
                            width: 512
                            breakpoints: [64, 96, 128, 256] # small, perfect for thumbs
                        )
                    }
                }
            }
            # Public-only assets (e.g. SVG); no childImageSharp here
            assets: allFile(filter: { extension: { in: ["svg"] } }) {
                nodes {
                    relativePath
                    name
                    extension
                    publicURL
                }
            }
        }
    `);

    const siteUrl: string = data?.site?.siteMetadata?.siteUrl || "";

    // Build quick lookups
    const byRel = new Map<string, RasterEntry>();
    const byBase = new Map<string, RasterEntry>();
    for (const n of data.images.nodes as Array<{
        relativePath: string;
        name: string;
        extension: string;
        publicURL: string | null;
        childImageSharp: { gatsbyImageData: IGatsbyImageData } | null;
    }>) {
        if (!n.childImageSharp) continue;
        const rel = stripLeading(n.relativePath);
        const base = `${n.name}.${n.extension}`.toLowerCase();
        const entry: RasterEntry = {
            rel,
            base,
            gatsby: n.childImageSharp.gatsbyImageData,
            publicURL: n.publicURL ?? null,
        };
        byRel.set(rel, entry);
        byBase.set(base, entry);
        // Allow both "images/…" and plain "…"
        if (!byRel.has(`images/${rel}`)) byRel.set(`images/${rel}`, entry);
    }

    const publicByRel = new Map<string, PublicEntry>();
    const publicByBase = new Map<string, PublicEntry>();
    for (const n of data.assets.nodes as Array<{
        relativePath: string;
        name: string;
        extension: string;
        publicURL: string | null;
    }>) {
        const rel = stripLeading(n.relativePath);
        const base = `${n.name}.${n.extension}`.toLowerCase();
        const entry: PublicEntry = { rel, base, publicURL: n.publicURL ?? null };
        publicByRel.set(rel, entry);
        publicByBase.set(base, entry);
        if (!publicByRel.has(`images/${rel}`)) publicByRel.set(`images/${rel}`, entry);
    }

    /** Resolve a ~96px absolute URL for thumbnails, or best possible fallback. */
    const thumb = (p?: string, opts?: { width?: number; preferWebp?: boolean }): string => {
        if (!p) return "";

        // A) Already an absolute URL? If it's a Gatsby /static URL, try to downshift via filename.
        if (isHttp(p)) {
            const mStatic = p.match(/\/static\/[^/]+\/[^/]+\/([^/?#]+)$/i);
            if (mStatic) {
                const base = mStatic[1].toLowerCase();
                const entry = byBase.get(base);
                if (entry) {
                    const chosen = pickThumbFromGatsby(entry.gatsby, opts);
                    if (chosen) return returnAsPath(chosen);
                }
            }
            // Otherwise, use the absolute URL as-is (could be Cloudinary etc.)
            return p;
        }

        // B) Relative raster (processed)
        const clean = stripLeading(p);
        const raster =
            byRel.get(clean) ||
            byRel.get(`images/${clean}`) ||
            byRel.get(clean.replace(/^images\//, "")) ||
            null;

        if (raster) {
            const chosen = pickThumbFromGatsby(raster.gatsby, opts);
            if (chosen) return returnAsPath(chosen);

            // fallback to its publicURL if available
            if (raster.publicURL) return returnAsPath(raster.publicURL);
        }

        // C) Public-only asset (e.g., SVG)
        const pub =
            publicByRel.get(clean) ||
            publicByRel.get(`images/${clean}`) ||
            publicByRel.get(clean.replace(/^images\//, "")) ||
            null;

        if (pub?.publicURL) return returnAsPath(pub.publicURL);

        // D) Last resort: return the raw path (browser will resolve)
        return clean.startsWith("/") ? clean : `/${clean}`;
    };

    return { thumb };
}
